import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, KeyRound, ShieldCheck, EyeOff } from "lucide-react";

const inputCls = "bg-slate-900 border-slate-700 text-slate-100";

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/team").then((r) => setMembers(r.data)).catch(() => setMembers([]));
  useEffect(() => { load(); }, []);

  const addAgent = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) return toast.error("Fill in all fields");
    setBusy(true);
    try {
      await api.post("/team", form);
      toast.success(`Agent account created for ${form.name}`);
      setAddOpen(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (m) => {
    const pw = window.prompt(`New password for ${m.name}? (min 6 characters)`);
    if (!pw) return;
    try {
      await api.put(`/team/${m.id}`, { password: pw });
      toast.success("Password updated");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/team/${m.id}`);
      toast.success("Agent removed");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-indigo-400 mb-1">User management</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50">Team</h1>
        </div>
        <Button data-testid="add-agent-button" onClick={() => setAddOpen(true)} className="bg-indigo-500 hover:bg-indigo-600 text-white">
          <Plus size={15} className="mr-1.5" /> Add agent
        </Button>
      </div>

      <div className="bg-[#111827] border border-slate-800 rounded-xl p-4 flex items-start gap-3">
        <EyeOff size={16} className="text-amber-400 mt-0.5 shrink-0" />
        <p className="text-xs text-slate-400">
          <span className="text-slate-200 font-medium">Agents</span> can manage customers, audiences and run campaigns — but customer <span className="text-slate-200">phone numbers and email IDs are masked</span> for them everywhere (lists, previews, reports, logs).
        </p>
      </div>

      <div className="space-y-2">
        {members === null ? null : members.map((m) => (
          <div key={m.id} data-testid="team-member-row" className="stat-card p-4 flex items-center justify-between gap-3 fade-up">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${m.role === "admin" ? "bg-indigo-500/15 text-indigo-400" : "bg-slate-700/40 text-slate-300"}`}>
                {m.role === "admin" ? <ShieldCheck size={16} /> : m.name?.[0]?.toUpperCase() || "A"}
              </div>
              <div className="min-w-0">
                <div className="text-sm text-slate-100 truncate">{m.name} {m.id === user?.id && <span className="text-slate-500 text-xs">(you)</span>}</div>
                <div className="text-xs text-slate-500 truncate">{m.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge data-testid="team-member-role" className={`border ${m.role === "admin" ? "bg-indigo-500/15 text-indigo-400 border-indigo-500/30" : "bg-slate-500/15 text-slate-300 border-slate-600"}`}>{m.role}</Badge>
              {m.role === "agent" && (
                <>
                  <button data-testid="agent-reset-password-button" title="Reset password" onClick={() => resetPassword(m)} className="text-slate-400 hover:text-indigo-400 p-1.5"><KeyRound size={15} /></button>
                  <button data-testid="agent-delete-button" title="Remove agent" onClick={() => remove(m)} className="text-slate-400 hover:text-rose-400 p-1.5"><Trash2 size={15} /></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Add agent</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-slate-400 text-xs">Name</Label><Input data-testid="agent-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
            <div className="space-y-1"><Label className="text-slate-400 text-xs">Email (their login)</Label><Input data-testid="agent-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></div>
            <div className="space-y-1"><Label className="text-slate-400 text-xs">Password</Label><Input data-testid="agent-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 6 characters" className={inputCls} /></div>
            <Button data-testid="agent-create-button" onClick={addAgent} disabled={busy} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
              {busy ? "Creating…" : "Create agent account"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
